// Completed-predicate classifier — the tail of a WHERE / HAVING / ON predicate,
// plus the operator boundaries inside one (`IS ▮`, `IS NOT ▮`, a BETWEEN low
// bound). Reused for a CASE branch's WHEN predicate and an UPDATE's SET
// assignment, which have the same "operand after a comparison operator" shape.
//
// Split out of `nextKeywordSlot.ts` (which owns the clause dispatch and calls
// in here) to keep each classifier module readable. Same CONFIDENT-ONLY
// contract: return `null` unless the scan is clearly at the named boundary.
// PURE text scan — no parse, no async, safe on the hot path.

import { matchBetweenBound } from '@/features/sql-grammar/document/editorContextSlot.betweenScan';

import type { NextKeywordSlot } from './legalNextKeywords';
import {
  IDENT_CHAR,
  hasTrailingWhitespace,
  scanRegion,
  trailingWord,
  wordBefore,
  type ScanOptions,
} from './slotTextScan';

/** Keywords that, as the last token before the cursor, mean the predicate is
 *  still being built (an operand or sub-expression follows). */
const PREDICATE_OPEN_KEYWORDS: ReadonlySet<string> = new Set([
  'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN', 'IS', 'ON', 'WHERE', 'HAVING', 'BY',
]);

/** Values that, as a completed-predicate tail token, are confident operands. */
const PREDICATE_OPERAND_KEYWORDS: ReadonlySet<string> = new Set(['NULL', 'TRUE', 'FALSE']);

/** Operator-ish trailing chars that mark a predicate as still open (an operand
 *  is expected next) — so the cursor is NOT after a completed predicate. */
const OPEN_OPERATOR_CHARS: ReadonlySet<string> = new Set([
  '=', '<', '>', '+', '-', '*', '/', '%', '(', ',', '.',
]);

// ─── predicate tails (where / having / on) ──────────────────────────────────

export function classifyPredicateTail(
  text: string,
  offset: number,
  statementStart: number,
  slot: NextKeywordSlot,
  opts: ScanOptions,
): NextKeywordSlot | null {
  if (!hasTrailingWhitespace(text, offset, statementStart)) return null;
  // Comment- and literal-aware: `WHERE a = 1 /* note */ ▮` must read the `1`,
  // not the comment's closing slash, as the last thing the user wrote.
  const scan = scanRegion(text, statementStart, offset, opts);
  const end = scan.lastCodeEnd;
  if (end <= statementStart) return null;
  const prev = scan.lastCodeChar;

  // Open operator (`x = `, `x + `, `(`, `,`) → operand still expected.
  if (OPEN_OPERATOR_CHARS.has(prev)) return null;

  // A closing paren tail is NOT a confident completed predicate: `COUNT(*) `,
  // `foo(x) `, `(a+b) ` are operands still awaiting a comparison. Bail to the
  // null-fallback (the resolver keeps today's full-list behavior).
  if (prev === ')') return null;

  if (IDENT_CHAR.test(prev)) {
    const w = trailingWord(text, end, statementStart);
    if (!w) return null;
    const up = w.word.toUpperCase();
    // Operand keyword (`IS NULL`, `IS TRUE`, `= NULL`) → completed predicate,
    // but ONLY when anchored by a preceding `IS` or comparison operator.
    if (PREDICATE_OPERAND_KEYWORDS.has(up)) {
      if (operandAnchoredByIsOrComparison(text, w.start, statementStart)) {
        return slot;
      }
      return null;
    }
    // `IS ▮` / `IS NOT ▮` — a null / boolean / JSON test follows, never an
    // identifier, so the schema flood these used to get is always wrong.
    if (up === 'IS') {
      return 'after-is';
    }
    if (up === 'NOT' && wordBefore(text, w.start, statementStart) === 'IS') {
      return 'after-is-not';
    }
    // An open predicate keyword (`AND`, `IS`, `BY`, …) → still building. Bail.
    if (PREDICATE_OPEN_KEYWORDS.has(up)) return null;
    // A bare/dotted identifier operand: confident ONLY when it is the
    // right-hand operand of a COMPARISON operator (`col = b.id `). When it is
    // the left-hand column awaiting an operator (`WHERE col `) we are NOT at a
    // completed predicate → null (confident-only).
    const identStart = operandStartBefore(text, end, statementStart);
    if (precededByComparisonOp(text, identStart, statementStart)) {
      return slot;
    }
    return betweenBoundSlot(text, identStart, statementStart, slot);
  }

  // Number / string / delimited-ident operand tail — confident only when the
  // whole operand is the right-hand side of a comparison operator (`x = 1 `,
  // `x = 'a' `, `[a] = [b] `).
  const opStart = operandStartBefore(text, end, statementStart);
  if (opStart !== null && precededByComparisonOp(text, opStart, statementStart)) {
    return slot;
  }
  return betweenBoundSlot(text, opStart, statementStart, slot);
}

/** Classifies a completed operand that sits inside a `BETWEEN … AND …` range.
 *  After the LOW bound exactly one keyword is legal (`AND`); after the HIGH
 *  bound the predicate is complete and takes the caller's clause-tail slot.
 *  Any other shape declines, leaving today's behavior. */
function betweenBoundSlot(
  text: string,
  operandStart: number | null,
  statementStart: number,
  completedSlot: NextKeywordSlot,
): NextKeywordSlot | null {
  if (operandStart === null) return null;
  const bound = matchBetweenBound(text, operandStart, statementStart);
  if (!bound) return null;
  return bound.bound === 'low' ? 'after-between-low' : completedSlot;
}

/** Start offset of the operand ending at `end` (one past its last char), or null
 *  when the tail is not a recognizable single operand. Handles: bare/dotted
 *  identifiers (incl. delimited segments), number literals, and single-quoted
 *  string literals. A `)` tail returns null (handled conservatively upstream). */
function operandStartBefore(text: string, end: number, statementStart: number): number | null {
  if (end <= statementStart) return null;
  const prev = text[end - 1] ?? '';
  // Single-quoted string literal.
  if (prev === "'") {
    let j = end - 1; // skip the closing quote
    while (j > statementStart && text[j - 1] !== "'") j -= 1;
    return j > statementStart ? j - 1 : null; // include the opening quote
  }
  // Number literal (digits + dot).
  if (/[0-9]/.test(prev)) {
    let j = end;
    while (j > statementStart && /[0-9.]/.test(text[j - 1] ?? '')) j -= 1;
    return j;
  }
  // Bare/dotted/delimited identifier reference.
  if (IDENT_CHAR.test(prev) || prev === ']' || prev === '"' || prev === '`') {
    return dottedReferenceStart(text, end, statementStart);
  }
  return null;
}

/** Walks back from `end` over a `<seg>(.<seg>)*` reference (segments bare or
 *  delimited) and returns the start offset of the whole reference. */
function dottedReferenceStart(text: string, end: number, statementStart: number): number {
  let i = consumeSegment(text, end, statementStart);
  for (;;) {
    let k = i;
    while (k > statementStart && /\s/.test(text[k - 1] ?? '')) k -= 1;
    if (k > statementStart && text[k - 1] === '.') {
      let s = k - 1;
      while (s > statementStart && /\s/.test(text[s - 1] ?? '')) s -= 1;
      i = consumeSegment(text, s, statementStart);
      continue;
    }
    return i;
  }
}

/** Consumes one identifier segment (bare or delimited) ending at `segEnd` and
 *  returns its start offset. */
function consumeSegment(text: string, segEnd: number, statementStart: number): number {
  const c = text[segEnd - 1] ?? '';
  if (c === ']' || c === '"' || c === '`') {
    const open = c === ']' ? '[' : c;
    let j = segEnd - 1;
    while (j > statementStart && text[j - 1] !== open) j -= 1;
    return j > statementStart ? j - 1 : statementStart;
  }
  let j = segEnd;
  while (j > statementStart && IDENT_CHAR.test(text[j - 1] ?? '')) j -= 1;
  return j;
}

/** True when the non-whitespace char immediately before `operandStart` is the
 *  tail of a COMPARISON operator (`=`, `<`, `>`, and thus `<=`/`>=`/`<>`/`!=`).
 *  Arithmetic operators (`+ - * / %`) do NOT count — `a + b ` is not a
 *  completed predicate. `null` operandStart → false.
 *
 *  A trailing `>` is only a comparison when it is a STANDALONE `>`/`>=`/`<>`.
 *  When it is the tail of a JSON / arrow operator (`->`, `->>`, `#>`, `#>>`)
 *  the left side is still an un-compared expression (`data -> 'k' `), so we
 *  must NOT report a completed comparison. */
function precededByComparisonOp(
  text: string,
  operandStart: number | null,
  statementStart: number,
): boolean {
  if (operandStart === null) return false;
  let i = operandStart;
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  if (i <= statementStart) return false;
  const op = text[i - 1] ?? '';
  if (op === '=' || op === '<') return true;
  if (op === '>') {
    // Disambiguate a standalone `>`/`>=`/`<>` (comparison) from the `>` tail of
    // an arrow/JSON operator (`->`, `->>`, `#>`, `#>>` — NOT a comparison).
    // `>=`/`<=` end in `=` and never reach here. The char(s) before this `>`:
    //   `->` / `#>`  → prev char is `-` or `#`            → arrow/JSON op
    //   `->>` / `#>>`→ prev char is `>`, then `-` or `#`  → arrow/JSON op
    //   `<>`         → prev char is `<`                   → comparison
    const p1 = text[i - 2] ?? '';
    if (p1 === '-' || p1 === '#') return false; // `->`, `#>`
    if (p1 === '>') {
      const p2 = text[i - 3] ?? '';
      if (p2 === '-' || p2 === '#') return false; // `->>`, `#>>`
    }
    return true; // standalone `>` or `<>`
  }
  return false;
}

/** True when the operand keyword (`NULL`/`TRUE`/`FALSE`) starting at
 *  `wordStart` is anchored by a preceding `IS` (incl. `IS NOT`) or a comparison
 *  operator — the canonical predicate completions. */
function operandAnchoredByIsOrComparison(
  text: string,
  wordStart: number,
  statementStart: number,
): boolean {
  if (precededByComparisonOp(text, wordStart, statementStart)) return true;
  // Look at the keyword(s) before the operand: `IS` or `IS NOT`.
  let i = wordStart;
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  const w1 = trailingWord(text, i, statementStart);
  if (!w1) return false;
  const up1 = w1.word.toUpperCase();
  if (up1 === 'IS') return true;
  if (up1 === 'NOT') {
    let j = w1.start;
    while (j > statementStart && /\s/.test(text[j - 1] ?? '')) j -= 1;
    const w2 = trailingWord(text, j, statementStart);
    return !!w2 && w2.word.toUpperCase() === 'IS';
  }
  return false;
}
